import {
  STATUS_ACTIVE,
  STATUS_INACTIVE,
  PENDING,
  ACTIVE,
  CANCELLED,
  SUSPENDED,
  EMAIL_VERIFIED,
  EMAIL_UNVERIFIED,
  PHANTOM_USER,
  REGISTERED_USER,
  BETA,
  TRIAL,
  RELEASE,
  TRAINING,
  INSTITUTION_LICENSE,
  INDIVIDUAL_LICENSE,
  USER_ACTIVE,
  USER_INACTIVE,
  licensedProductRadios,
  countryFilters,
} from './index';

// Minimal shape of the domain objects passed to filter `func` predicates.
// Mirrors the `FilterItem` shape defined in `./index` (which is not exported);
// structural typing keeps the imported filter functions assignable here.
interface FilterItem {
  productAccess: {
    name?: string;
    activityState?: string;
    status?: string;
    type?: string;
  };
  costCenter: {
    type?: string;
    countryCode?: string;
  };
  verified?: boolean;
  phantom?: boolean;
  accountState?: string;
  countryCode?: string;
}

// Predicate applied to a single table row (user or license object).
type FilterFunc = (item: FilterItem) => boolean;

// Minimal shape of a filter definition from `./index`, covering the fields
// actually read here. `./index` is still untyped JS, so these values arrive as
// `any` and flow into this interface without friction.
interface FilterConfig {
  label: string;
  id: string;
  func: FilterFunc;
}

// Define associated toggles
export const TOGGLE_PAIRS: FilterConfig[][] = [
  [USER_ACTIVE, USER_INACTIVE],
  [EMAIL_VERIFIED, EMAIL_UNVERIFIED],
  [PHANTOM_USER, REGISTERED_USER],
];

// This array of arrays defines the structure in which filters are composed. Not all filters will be
// active at once: if a product such as FlowJo is active and SeqGeq is not, the first child array will
// just be a FlowJo.
//
// Inner arrays are "or" groups; only one function must pass for the whole inner array to pass. The
// outer array is an "and" group: all inner arrays must pass for the "combined filter" to pass.
// See `utils.combineFilters`
const buildFilterGroups = (): FilterConfig[][] => [
  licensedProductRadios(),
  [STATUS_ACTIVE, STATUS_INACTIVE],
  [PENDING, ACTIVE, CANCELLED, SUSPENDED],
  [USER_ACTIVE, USER_INACTIVE],
  [EMAIL_VERIFIED, EMAIL_UNVERIFIED],
  [PHANTOM_USER, REGISTERED_USER],
  [RELEASE, TRIAL, TRAINING, BETA],
  [INSTITUTION_LICENSE, INDIVIDUAL_LICENSE],
  countryFilters(),
];

// Formats the `FILTER_GROUPS` structure into raw filters for a given activity state
export const getActiveFilterGroups = (
  isFilterActive: (filter: FilterConfig) => boolean,
): FilterFunc[][] =>
  buildFilterGroups()
    .map((filterGroup) =>
      filterGroup
        // Remove inactive filters
        .filter(isFilterActive)
        // For each active filter, return only the function
        .map((filterConfig) => filterConfig.func),
    )
    .filter((filterGroup) => !!filterGroup.length);
